import { Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { reduceResolution, wbp } from 'Theme';
import { VaccinesExpirationField } from './VaccinesExpirationField';
import { VaccinesFileField } from './VaccinesFileField';
import { VaccinesStatusField } from './VaccinesStatusField';
import { VaccineTableDisplayRow } from './pendingVaccineApprovals';

interface VaccinesTableRowProps {
    row: VaccineTableDisplayRow;
    hasActionsColumn?: boolean;
    canReview?: boolean;
    onAccept?: (approvalId: number) => Promise<void>;
    onDecline?: (approvalId: number) => Promise<void>;
    className?: string;
    last?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(10)}px 0`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '10px 0'
            },
            [theme.breakpoints.down('md')]: {
                padding: '12px 0'
            }
        },
        rowBorder: {
            borderBottom: '1px solid #EFEFEF'
        },
        name: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            [theme.breakpoints.down(wbp)]: {
                lineHeight: `${reduceResolution(24)}px`,
                fontSize: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                lineHeight: '24px',
                fontSize: '15px'
            }
        },
        containerVaccinesContent: {
            [theme.breakpoints.down('md')]: {
                'flexDirection': 'column',
                'flexWrap': 'nowrap',
                'alignItems': 'stretch',
                'position': 'relative',
                'textAlign': 'left',
                '& > .MuiGrid-item': {
                    width: '100%',
                    maxWidth: '100%'
                }
            }
        },
        vaccineName: {
            fontWeight: 500,
            [theme.breakpoints.down('md')]: {
                fontSize: '16px',
                fontWeight: 500,
                marginBottom: '10px',
                paddingRight: '90px',
                textAlign: 'left'
            }
        },
        vaccineExpiration: {
            [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                marginBottom: '10px',
                textAlign: 'left'
            }
        },
        vaccineStatus: {
            [theme.breakpoints.down('md')]: {
                'fontSize': '14px',
                'display': 'flex',
                'justifyContent': 'flex-start',
                '& > div': {
                    justifyContent: 'flex-start'
                }
            }
        },
        vaccineFile: {
            [theme.breakpoints.down('md')]: {
                'position': 'absolute',
                'top': '5px',
                'right': '0px',
                'width': 'auto !important',
                'maxWidth': 'none !important',
                'paddingRight': '0px',
                '& p': {
                    width: 'auto'
                },
                '& div': {
                    paddingRight: '0px',
                    justifyContent: 'flex-end'
                }
            }
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            [theme.breakpoints.down('md')]: {
                marginTop: 12,
                justifyContent: 'stretch',
                gap: 10,
                width: '100%'
            }
        },
        actionButton: {
            minWidth: 96,
            [theme.breakpoints.down('md')]: {
                flex: 1,
                minWidth: 0,
                width: '100%'
            }
        }
    })
);

export const VaccinesTableRow: FunctionComponent<VaccinesTableRowProps> = props => {
    const classes = useStyles();
    const [actionLoading, setActionLoading] = useState<'accept' | 'decline' | null>(null);
    const pendingApproval = props.row.pendingApproval;
    const showActions = Boolean(pendingApproval && props.canReview);
    const hasActionsColumn = Boolean(props.hasActionsColumn);

    const handleAccept = async (event: MouseEvent) => {
        event.stopPropagation();
        if (!pendingApproval || !props.onAccept) return;
        setActionLoading('accept');
        try {
            await props.onAccept(pendingApproval.id);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDecline = async (event: MouseEvent) => {
        event.stopPropagation();
        if (!pendingApproval || !props.onDecline) return;
        setActionLoading('decline');
        try {
            await props.onDecline(pendingApproval.id);
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <Grid
            container
            className={clsx(
                classes.row,
                classes.containerVaccinesContent,
                { [classes.rowBorder]: !props.last },
                classes.name,
                props.className
            )}
            alignItems="center"
        >
            <Grid
                item
                xs={12}
                sm={hasActionsColumn ? 2 : 3}
                className={classes.vaccineName}
            >
                {props.row.vaccine.type?.name}
            </Grid>
            <Grid
                item
                xs={12}
                sm={hasActionsColumn ? 2 : 3}
                className={classes.vaccineExpiration}
            >
                <VaccinesExpirationField
                    preLabel="Exp Date"
                    vaccine={props.row.vaccine}
                />
            </Grid>
            <Grid
                item
                xs={12}
                sm={hasActionsColumn ? 3 : 4}
                className={classes.vaccineStatus}
            >
                <VaccinesStatusField vaccine={props.row.vaccine} />
            </Grid>
            <Grid
                item
                xs={12}
                sm={2}
                className={classes.vaccineFile}
            >
                <VaccinesFileField vaccine={props.row.vaccine} />
            </Grid>
            {hasActionsColumn && (
                <Grid
                    item
                    xs={12}
                    sm={3}
                    className={classes.actions}
                >
                    {showActions && (
                        <>
                            <Button
                                label="Decline"
                                variant="danger"
                                size="small"
                                className={classes.actionButton}
                                isLoading={actionLoading === 'decline'}
                                disabled={actionLoading !== null}
                                onClick={handleDecline}
                            />
                            <Button
                                label="Accept"
                                variant="green"
                                size="small"
                                className={classes.actionButton}
                                isLoading={actionLoading === 'accept'}
                                disabled={actionLoading !== null}
                                onClick={handleAccept}
                            />
                        </>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default VaccinesTableRow;
